/* global __SHORTCUTS_ENABLED__:false */
// data models
import PlayerModel from 'app/model/PlayerModel';
import StateController from './StateController';
import {
  STATE_BOOT,
  STATE_CUTSCENE,
  STATE_LEVEL_HIDE,
  STATE_LEVEL_SEEK,
  STATE_LOCATION_LOADER,
  STATE_TWO_PLAYERS_SETUP,
  STATE_LEVEL_TWO_PLAYERS,
  STATE_WELCOME,
} from '../constants/StateConstants';
import {
  LOCATION_AUTUMN,
  LOCATION_SUMMER,
  LOCATION_WINTER,
  MODE_HIDE,
  MODE_SEEK,
  PLAYER_SINGLE,
  PLAYER_TWO,
} from '../constants/AppConstants';
import CutsceneModel from '../model/CutsceneModel';
import Shortcuts, { ShortcutStub } from 'app/shortcuts/Shortcuts';
import ShortcutsModel from 'app/shortcuts/ShortcutsModel';
import Transition from '../ui/Transition';

export default class GameController {
  constructor(game) {
    this.game = game;
  }

  init(gameData) {
    this.gameData = gameData;

    //
    this.cutscenes = new CutsceneModel(this.game.cache.getJSON('data_cutscenes'));

    // event listeners
    // TODO : Should be in game ?
    this.onGamePaused = new Phaser.Signal();
    this.onGameResume = new Phaser.Signal();

    // controllers
    this.stateController = new StateController(this.game);

    // properties
    this.seekerId = 'elinor'; // id of current seeker - by default elinor
    this.nextSeekerId = 'elinor'; // id of first found character - seeker in next round
    this.levelId = 0; // current level
    this.location = LOCATION_SUMMER; // current location - by default SUMMER
    this.playerMode = PLAYER_SINGLE; // selected player mode - 1 or 2
    this.gameMode = MODE_SEEK; // current game mode - HIDE or SEEK
    this.player = null; // player model
    this.twoPlayersModel = null;
    this.replays = 0; // number of level replays

    //
    this.shortcutsModel = new ShortcutsModel();
    if (__SHORTCUTS_ENABLED__) {
      this.shortcuts = new Shortcuts(this.game, { model: this.shortcutsModel, isVisible: false });
      this.shortcuts.create();
    } else {
      this.shortcuts = new ShortcutStub();
    }
    //
    this.startNewGame();
  }

  startBoot() {
    this.game.state.start(STATE_BOOT, true, true);
  }

  startLoad() {}

  reloadApp() {
    this.startBoot();
  }

  /*
  ======================================================================
    TRIGGER GAME STATES
  ======================================================================
  */

  showWelcomeScreen(endOfLevel = false) {
    this.shortcuts.reset();
    // loads and displays the welcome screen state
    if (this.game.paused) {
      this.resumeGame();
    }

    this.startState(STATE_WELCOME, { endOfLevel: endOfLevel });
  }

  /*
  ======================================================================
    SHORTCUTS
  ======================================================================
  */

  hasLevel(id) {
    return this.gameData.location[this.location][this.playerMode].levels.reduce((acc, item) => {
      return item.id === id ? true : acc;
    }, false);
  }

  scNextLevel() {
    this.endLevel();
    return this.levelId;
  }

  scLaunchLevel({ levelID }) {
    if (this.hasLevel(levelID)) {
      this.levelId = levelID;
      this.initNewLevel(this.levelId);
      if (this.levelData.cutscene) {
        this.startCutscene(this.levelData.cutscene);
      } else {
        this.startLevel();
      }
    } else {
      console.warn(`<> ${levelID} doesnt appear to exist`);
    }
  }

  /*
  ====================================================================================
    LOAD / SAVE PLAYER DATA
  ====================================================================================
  */

  startNewGame() {
    this.setPlayer();
  }

  setPlayer() {
    this.player = new PlayerModel();
  }

  /*
  ======================================================================
    VARIOUS
  ======================================================================
  */
  setLocation(ind) {
    const locations = [LOCATION_SUMMER, LOCATION_AUTUMN, LOCATION_WINTER];
    this.location = locations[ind];
    this.locationInd = ind;
    // this.location = LOCATION_SUMMER;
  }

  isNextLevelHiding() {
    const levels = this.gameData.location[this.location][this.playerMode].levels;
    if (this.levelId < levels.length - 1) {
      return levels[this.levelId + 1].mode === 'hide';
    }

    return false;
  }

  isFirstLevel() {
    return (
      this.location === LOCATION_SUMMER && this.levelId === 0 && this.playerMode === PLAYER_SINGLE
    );
  }

  getTransition() {
    this.transition = new Transition(this.game);
    return this.transition;
  }

  /*
  ======================================================================
    LAUNCH CUTSCENE
  ======================================================================
  */

  startCutscene(id, isEndScene = false) {
    this.cutsceneId = id;

    const data = this.cutscenes.getScene(id);

    // start the state
    this.startState(STATE_CUTSCENE, { id, data, isEndScene });
  }

  getCutsceneData(id) {
    const captions = this.cutscenes.getCaptions(id);
    const data = this.cutscenes.getScene(id);
    return { id, captions, data };
  }

  /*
  ======================================================================
    LAUNCH LEVEL
  ======================================================================
  */

  initNewLevel(id) {
    this.shortcuts.showLevelSettings({ level: id });

    // for 2 players mode level id is always 0
    if (this.playerMode === PLAYER_TWO) {
      id = 0;
    } else {
      this.player.lastLevel[this.locationInd] = id;
      this.player.save();
    }

    //
    this.levelId = id;
    this.levelData = this.gameData.location[this.location][this.playerMode].levels.find(
      (e) => e.id === id
    );
    this.gameMode = this.levelData.mode === 'seek' ? MODE_SEEK : MODE_HIDE;

    //
    if (this.playerMode === PLAYER_SINGLE) {
      this.seekerId = this.levelData.seeker ? this.levelData.seeker : this.nextSeekerId;
    } else {
      // for 2 players mode, choose random seeker
      this.setRandomSeeker();
    }
  }

  setRandomSeeker() {
    const ind = Math.floor(Math.random() * this.levelData.characters.length);
    this.seekerId = this.levelData.characters[ind].id;
  }

  startGame() {
    this.shortcuts.addLevelData({
      levelData: this.gameData.location[this.location][this.playerMode].levels,
    });
    // use last played level to start the game
    this.loadLevel(this.player.getLastLevel(this.locationInd));
  }

  loadLevel(id) {
    this.initNewLevel(id);

    //
    this.startState(STATE_LOCATION_LOADER, {
      location: this.location,
      playerMode: this.playerMode,
    });
  }

  // called from LocationLoader when all assets are loaded
  startLocation() {
    this.replays = 0;
    const lastLevel = this.player.getLastLevel(this.locationInd);
    const canDisplayCutscene = this.levelId !== 0 || lastLevel === 0;
    if (this.levelData.cutscene && canDisplayCutscene) {
      this.startCutscene(this.levelData.cutscene);
    } else {
      this.startLevel();
    }
  }

  // called from Welcome screen
  startLevel() {
    if (this.playerMode === PLAYER_SINGLE) {
      // single player mode
      if (this.gameMode === MODE_SEEK) {
        // load seek level
        this.startState(STATE_LEVEL_SEEK, {
          levelData: this.levelData,
          seekerId: this.seekerId,
        });
      } else {
        // load hide level
        this.startState(STATE_LEVEL_HIDE, {
          levelData: this.levelData,
          seekerId: this.seekerId,
        });
      }
    } else {
      // two players mode
      this.startState(STATE_TWO_PLAYERS_SETUP, {
        levelData: this.levelData,
        seekerId: this.seekerId,
      });
    }
  }

  /*
  =========================================================================================================
    TRIGGER STATE CHANGE
  =========================================================================================================
  */

  startState(stateid, stateprops) {
    this.game.controller.stateController.startState({
      nextState: stateid,
      nextStateParams: [true, false, stateprops],
    });
  }

  endCutscene(isEndScene) {
    if (isEndScene) {
      if (this.locationInd === 2) {
        // it was last end cutscene -> return home
        this.showWelcomeScreen(false);
      } else {
        // go to next level in new location
        this.nextLevel();
      }
    } else {
      this.startLevel();
    }
  }

  endLevel() {
    this.replays = 0;

    if (
      this.levelData.cutscene_end &&
      !this.game.controller.player.wasCutsceneDisplayed(this.levelData.cutscene_end)
    ) {
      // if (this.levelData.cutscene_end) {
      this.startCutscene(this.levelData.cutscene_end, true);
    } else {
      this.nextLevel();
    }
  }

  nextLevel() {
    this.replays = 0;

    const levels = this.gameData.location[this.location][this.playerMode].levels;
    if (this.levelId < levels.length - 1) {
      // there are levels in current location
      this.levelId++;

      this.initNewLevel(this.levelId);

      //
      if (this.levelData.cutscene) {
        this.startCutscene(this.levelData.cutscene);
      } else {
        // this.loadLevel(this.levelId);
        this.startLevel();
      }
    }
    // end of levels
    // if new location unlocked -> change location to new and load 1st level
    else if (
      this.locationInd + 1 === this.player.unlockedLocations &&
      this.player.unlockedLocations < 3
    ) {
      this.player.unlockedLocations++;
      this.player.save();
      this.setLocation(this.locationInd + 1);
      this.startGame();
    } else {
      // play previous level to stay in loop
      // this.showWelcomeScreen(true); // return to home
      this.levelId--;
      this.initNewLevel(this.levelId);
      this.startLevel();
    }
  }

  replayLevel() {
    this.replays++;
    this.startLevel();
  }

  replayTwoPlayers() {
    // this.setRandomSeeker();

    this.twoPlayersModel.hidingPlayerId = 0;
    this.twoPlayersModel.isReplay = true;
    this.seekerId = this.twoPlayersModel.getSeekingPlayerIcon();

    this.startState(STATE_LEVEL_TWO_PLAYERS, {
      levelData: this.levelData,
      seekerId: this.seekerId,
    });
  }

  /*
  ====================================================================================
    PAUSE / RESUME
  ====================================================================================
  */

  pauseGame() {
    if (!this.game.paused) {
      this.onGamePaused.dispatch();
      this.game.paused = true;
      this.game.sounds.pauseAll();
    }
  }

  resumeGame() {
    if (this.game.paused) {
      this.onGameResume.dispatch();
      this.game.paused = false;
      this.game.sounds.resumeAll();
    }
  }
}
